import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import Cookies from 'js-cookie'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import AiBuildingIndicator from '@/components/AiBuildingIndicator.vue'
import BottleBotWidget from '@/components/catering/BottleBotWidget.vue'
import ConvertToNewEmail from '@/components/ConvertToNewEmail'
import OmniSearch from '@/components/OmniSearch'
import SetupDrawer from '@/components/setup/SetupDrawer.vue'
import { closeSetupDrawer } from '@/components/setup/setupDrawerOpen'
import { resetSetupState, setAssistantAvailable, useSetupState } from '@/composables/setupState'
import { botWidgetVisibility } from '@/helpers/botWidgetVisibility'
import { clearParsleyRun } from '@/helpers/parsleyRunTracker'
import { createRevelioKeydownHandler, useRevealed } from '@/helpers/revelio'
import convertEmailService from '@/services/convertEmail.service'
import api from '@/store/api'


export default {
  __name: 'App',
  setup(__props) {

const store = useStore()
const route = useRoute()
const isLoggedIn = computed(() => !!store.state.loggedInUser?.attributes || !!Cookies.get('auth_token'))
const isCatering = computed(() => store.getters.isCatering)
const showBottleBot = computed(() => store.getters.showBottleBot)
const showEcommerceBot = computed(() => store.getters.showEcommerceBot)
// Waits for the user to actually hydrate before mounting: the signup wizard and
// login render their own full-screen shells with their own top-right chrome, and
// polling a merchant endpoint from those routes is unauthenticated anyway.
const showAiBuilding = computed(() => !!store.state.loggedInUser?.attributes && !route.meta?.noAuth)
const { applicable: setupApplicable, ensure: ensureSetupState } = useSetupState((...args) => api.call(...args))
const showSetupDrawer = computed(
  () => !!store.state.loggedInUser?.attributes && !route.meta?.noAuth && setupApplicable.value
)

// Setup state lives outside vuex, so RESET_STATE cannot clear it: without this the
// next merchant on this tab reads the previous merchant's name, phone number and
// address, and ensure() short-circuits on that instead of fetching their own.
watch(
  () => store.state.loggedInUser?.id,
  (id, previous) => {
    if (previous && previous !== id) {
      closeSetupDrawer()
      resetSetupState()
      clearParsleyRun()
    }
    if (id) ensureSetupState()
  }
)
const channel = ref(null)

// Every unreleased surface listens to one flag, and App is the only place that
// hears the word - so it works on whatever page you happen to be on.
const revealed = useRevealed()
const botWidgets = computed(() => botWidgetVisibility(store.getters, revealed.value))
const revealKeydownHandler = createRevelioKeydownHandler()

// The merchant's own entitlement, not what is on screen: the drawer is their
// checklist and the step is theirs to finish, so a staff peek must neither add a
// step for them nor let it be ticked off on their behalf, and revelio must not
// move it either way. A system agent Bottle has switched off, and a platform the
// merchant is not looking at, both still leave it pointing at a button nobody has.
const assistantAvailable = computed(() => showBottleBot.value || showEcommerceBot.value)
watch(assistantAvailable, setAssistantAvailable, { immediate: true })

const convertModalCheckTrigger = ref(0)

const rawConvertMode = process.env.VUE_APP_CONVERT_TO_NEW_EMAIL || 'suggest'
const convertMode = ['link', 'suggest', 'force'].includes(rawConvertMode) ? rawConvertMode : 'suggest'

const showConvertToNewEmail = computed(() => {
  if (convertMode === 'link') return false
  // A franchise principal signs in with their own email; the prompt is the franchisee's to answer.
  if (store.getters.isFranchise) return false

  convertEmailService.dismissedAt.value
  convertModalCheckTrigger.value

  // Wait until user attributes are actually loaded before evaluating
  const userAttributes = store.state.loggedInUser?.attributes
  if (!userAttributes) return false

  const baseConditions = isLoggedIn.value && !route.meta?.noAuth && !userAttributes.converted_to_email_signin

  if (convertMode === 'force') return baseConditions
  return baseConditions && convertEmailService.shouldShow()
})

watch(
  () => route.fullPath,
  () => {
    convertModalCheckTrigger.value++
  }
)

let convertModalCheckInterval = null

// TODO(alicja): centralize init stuff in some other way
let pusherInitialized = false
window.pusherInit = () => {
  if (pusherInitialized || !Cookies.get('auth_token')) return
  if (!store.state.pusher) {
    store.commit(
      'SET_PUSHER',
      new Pusher(process.env.VUE_APP_PUSHER_KEY, {
        authEndpoint: process.env.VUE_APP_API_SERVER + 'pusher/auth',
        auth: { headers: { Authorization: `Bearer ${Cookies.get('auth_token')}` } }
      })
    )
  }
  channel.value = store.state.pusher.subscribe(store.getters.loggedInUserId + '-merchant-channel')
  channel.value.bind('data-download', data => {
    store.commit('RECEIVE_OBJECT', data)
  })
  pusherInitialized = true
}

onMounted(() => {
  window.addEventListener('keydown', revealKeydownHandler)
  convertEmailService.init()
  convertModalCheckInterval = setInterval(() => {
    convertModalCheckTrigger.value++
  }, 60 * 1000)
  if (!Cookies.get('auth_token')) return
  if (!store.state.loggedInUser.attributes && !store.state.franchise) store.dispatch('getLoggedInUser')
  pusherInit()
  ensureSetupState()
})

onUnmounted(() => {
  window.removeEventListener('keydown', revealKeydownHandler)
  if (convertModalCheckInterval) clearInterval(convertModalCheckInterval)
  store.state.pusher.unsubscribe(store.getters.loggedInUserId + '-merchant-channel')
})

return (_ctx, _cache) => {
  const _component_router_view = _resolveComponent("router-view")

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (showConvertToNewEmail.value)
      ? (_openBlock(), _createBlock(_unref(ConvertToNewEmail), {
          key: 0,
          mode: _unref(convertMode),
          onDismiss: _cache[0] || (_cache[0] = () => _unref(convertEmailService).dismiss())
        }, null, 8, ["mode"]))
      : _createCommentVNode("", true),
    _createVNode(_component_router_view),
    (showAiBuilding.value)
      ? (_openBlock(), _createBlock(AiBuildingIndicator, { key: 1 }))
      : _createCommentVNode("", true),
    (showSetupDrawer.value)
      ? (_openBlock(), _createBlock(SetupDrawer, { key: 2 }))
      : _createCommentVNode("", true),
    (!isCatering.value)
      ? (_openBlock(), _createBlock(_unref(OmniSearch), { key: 3 }))
      : _createCommentVNode("", true),
    (botWidgets.value.bottleBot)
      ? (_openBlock(), _createBlock(BottleBotWidget, {
          key: 4,
          namespace: "bottleBot"
        }))
      : _createCommentVNode("", true),
    (botWidgets.value.ecommerceBot)
      ? (_openBlock(), _createBlock(BottleBotWidget, {
          key: 5,
          namespace: "ecommerceBot"
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

}